'use client';

import Image from "next/image";
import Link from "next/link";
import { useTranslations } from 'next-intl';
import { montserratMedium, montserratSemi, montserratRegular } from "../fonts";

const currentYear = new Date().getFullYear();

const handleSmoothScroll = (targetId?: string) => (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
  if (!targetId?.startsWith("#")) return;
  event.preventDefault();
  const target = document.querySelector(targetId);
  if (target) {
    target.scrollIntoView({ behavior: "smooth" });
  }
};

const SiteFooter = () => {
  const t = useTranslations('home.footer');
  
  const footerSections = [
    {
      title: t('product'),
      links: [
        { label: t('launchApp'), href: "/maintenance" },
        { label: t('iosDownload'), href: "/download" },
        { label: t('androidDownload'), href: "/download" },
      ],
    },
    {
      title: t('developer'),
      links: [{ label: t('documentation'), href: "https://docs.xox.exchange/" }],
    },
    {
      title: t('about'),
      links: [
        { label: t('aboutXox'), href: "https://docs.xox.exchange/" },
        { label: t('privacyPolicy'), href: "https://docs.xox.exchange/about/privacy-policy" },
        { label: t('termsOfUse'), href: "https://docs.xox.exchange/about/terms-of-use" },
      ],
    },
  ];

  const socialLinks = [
    { name: t('social.x'), icon: "fa-brands fa-x-twitter", href: "https://x.com/xox_DEX" },
    { name: t('social.discord'), icon: "fa-brands fa-discord", href: "https://discord.gg/d8kUQq8Z" },
    { name: t('social.telegram'), icon: "fa-brands fa-telegram", href: "https://t.me/xoxdex" },
  ];
  
  const handleLaunchApp = (e: React.MouseEvent) => {
    e.preventDefault();
    window.open('https://app.xox.exchange/', '_self');
  };

  return (
    <footer
      id="footer"
      className="relative z-10 overflow-hidden bg-[var(--main-color)] px-6 py-16 text-white lg:px-12"
    >
      <Image
        src="/assets/logo-dot.webp"
        alt={t('logoDecorative')}
        width={600}
        height={320}
        className="pointer-events-none absolute bottom-0 right-0 w-full md:w-[75%] h-auto"
        aria-hidden="true"
        loading="lazy"
      />
      <div className="relative m-auto flex w-full max-w-7xl flex-col gap-12 min-h-[200px] md:min-h-[500px]">
        <div className="flex flex-col gap-8 md:flex-row items-center md:items-start md:justify-between">
          <div className="flex flex-1 flex-col gap-12 items-center">
            <Link href="/">
              <Image
                src="/assets/logo.webp"
                alt={t('logoAlt')}
                width={200}
                height={80}
                className="h-10 w-auto sm:h-12"
                priority
              />
            </Link>
            <div className="flex gap-6">
              {socialLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-white/60 bg-transparent text-xl text-white transition hover:border-white/100"
                  aria-label={link.name}
                >
                  <i className={link.icon} aria-hidden="true" />
                  <span className="sr-only">{link.name}</span>
                </a>
              ))}
            </div>
          </div>
          <div className="grid flex-1 gap-8 text-left sm:grid-cols-2 md:grid-cols-3 hidden md:grid">
            {footerSections.map((section) => (
              <div key={section.title} className="space-y-4">
                <p className={`${montserratSemi.className} text-sm uppercase tracking-widest text-white`}>
                  {section.title}
                </p>
                <ul className="space-y-3 text-sm text-white/60">
                {section.links.map((link) => (
                  <li key={link.label}>
                    {link.label === t('launchApp') ? (
                      <button
                        onClick={handleLaunchApp}
                        className={`${montserratRegular.className} cursor-pointer text-left text-white/60 transition hover:text-white`}
                      >
                        {link.label}
                      </button>
                    ) : link.href.startsWith("#") ? (
                      <button
                        onClick={handleSmoothScroll(link.href)}
                        className={`${montserratRegular.className} cursor-pointer text-left text-white/60 transition hover:text-white`}
                      >
                        {link.label}
                      </button>
                    ) : (
                      <Link
                        href={link.href}
                        className={`${montserratRegular.className} cursor-pointer transition hover:text-white`}
                        target={link.href.startsWith('http') ? '_blank' : undefined}
                        rel={link.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div className={`${montserratRegular.className} flex justify-center items-center gap-2 text-xs text-white md:absolute md:bottom-0 md:left-0`}>
          <span>{t('copyright', { year: currentYear })}</span>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;

